/**
 * levelQueueParams.ts — the single source of truth for "which queue is this
 * level?".
 *
 * A level's attacker queue is a pure function of three inputs:
 *
 *     generateRawQueue(grid, seed, difficulty, megaBlocks, blockadeWalls)
 *
 * so any two consumers that disagree about `seed` or `difficulty` are talking
 * about DIFFERENT games even though they name the same level. That is exactly
 * the bug this module exists to prevent: `audit-worker.ts` used to resolve
 * difficulty as a bare `getDifficultyForLevel(levelNum)` (the historical
 * positional tier table) while `audit-levels.ts`, `GameScene` and
 * `PerfectSolver/initialState` all used `level.difficulty ?? tier-table`. Every
 * shipped level bakes `difficulty`, so the parallel audit path — the documented
 * `--parallel` invocation — proved WIN/LOSS about a queue the game never
 * generates.
 *
 * Every consumer (runtime, audit, solver, correctness tools) MUST resolve both
 * values through {@link resolveLevelQueueParams} / {@link resolveLevelDifficulty}
 * / {@link resolveLevelSeed} rather than re-deriving them inline.
 *
 * ── The `gen` proof block ───────────────────────────────────────────────────
 *
 * The same failure mode came back with the level factory. A proven level bakes
 * a `gen` block (`src/levels/genWriter.ts`) recording the EXACT `(config, seed)`
 * the winnability proof was taken at, while its `difficulty` block still holds
 * whatever the level was originally generated with. `3425-fire-station-01-08`
 * is proven at `multiSlotChance 0.30 / multiSlotColorSplitChance 1`, but bakes
 * `difficulty.multiSlotChance 0` — so a resolver that reads `difficulty` alone
 * hands the game a queue the proof says nothing about.
 *
 * So `gen.config` WINS over `difficulty` for the four fields it carries, and
 * `gen.seed` wins over `seed`. See {@link resolveLevelDifficulty} for the exact
 * precedence.
 */

import { getDifficultyForLevel, type DifficultyConfig } from '../systems/LevelGenerator';
import { getLevelSeed, type ImageLevel } from './types';

/**
 * Positional level number used for the tier-table fallback when a level has no
 * baked `difficulty` and no natural position in the corpus (levels under
 * `src/levels/new/`, standalone boards, ArtPipeline output). Mid-game.
 *
 * Matches `audit-levels.ts` (`auditNewLevel`), `SolverState.initialState` and
 * `ArtPipeline` — all of which historically hardcoded `getDifficultyForLevel(50)`.
 */
export const NEW_LEVEL_DIFFICULTY_LEVEL = 50;

/** The subset of an ImageLevel needed to resolve its queue parameters. */
export type LevelQueueSource = Pick<ImageLevel, 'difficulty' | 'seed' | 'gen'> | undefined;

/** Resolved queue-generation inputs for a level. */
export interface LevelQueueParams {
  /** Attacker-composition knobs `generateRawQueue` / `LevelGenerator.fromGrid` take. */
  difficulty: DifficultyConfig;
  /** Queue-generation RNG seed. */
  seed: number;
}

/**
 * The difficulty config a level's queue is generated from.
 *
 * Precedence, highest first:
 *
 *  1. **`level.gen.config`** — the proven config, for the four fields it
 *     carries (`multiSlotChance`, `multiColorChance`, `ammoSurplusRatio`,
 *     `multiSlotColorSplitChance`). A level only has a `gen` block because the
 *     PerfectSolver proved it WIN *at that config*, so this is the one config
 *     the shipped level is known to be winnable at.
 *  2. **`level.difficulty`** — the baked block, for everything `gen.config`
 *     does not cover (`mysteryChance`, `frozenChance`, `minAmmo`, `maxAmmo`)
 *     and for all four overlaid fields on levels with no `gen`.
 *  3. **The positional tier table** — `fallbackLevelNum` only supplies this for
 *     levels that bake no `difficulty` at all (pass the level's 1-based corpus
 *     position, or {@link NEW_LEVEL_DIFFICULTY_LEVEL} for a level with none).
 *
 * `gen.config.numQueues` is deliberately NOT resolved here: `numQueues` is a
 * top-level field of the level, and `genWriter` rewrites it in place to match
 * the proof, so every consumer already reads the proven lane count off
 * `level.numQueues`.
 *
 * Fields are copied explicitly so the result is a plain `DifficultyConfig`
 * regardless of extra keys on the level file's literal.
 *
 * NOTE for probe/sweep callers: a helper that field-swaps this result back into
 * `level.difficulty` and re-resolves (`calibrate-lib.withCalibratedDifficulty`,
 * `sweep-winnability`/`queue-stats` `withSweptDifficulty`) MUST also drop `gen`
 * from the probe level, or the overlay re-applies and silently undoes the swap.
 */
export function resolveLevelDifficulty(
  level: Pick<ImageLevel, 'difficulty' | 'gen'> | undefined,
  fallbackLevelNum: number,
): DifficultyConfig {
  const baked = level?.difficulty;
  const base: DifficultyConfig = baked
    ? {
        multiSlotChance: baked.multiSlotChance,
        multiColorChance: baked.multiColorChance,
        mysteryChance: baked.mysteryChance,
        frozenChance: baked.frozenChance,
        minAmmo: baked.minAmmo,
        maxAmmo: baked.maxAmmo,
        ammoSurplusRatio: baked.ammoSurplusRatio,
        // Optional; undefined ⇒ the generator's 0.9 default, unchanged.
        multiSlotColorSplitChance: baked.multiSlotColorSplitChance,
      }
    : getDifficultyForLevel(fallbackLevelNum);

  const proven = level?.gen?.config;
  if (!proven) return base;

  return {
    ...base,
    multiSlotChance: proven.multiSlotChance,
    multiColorChance: proven.multiColorChance,
    ammoSurplusRatio: proven.ammoSurplusRatio,
    multiSlotColorSplitChance: proven.multiSlotColorSplitChance,
  };
}

/**
 * The queue-generation seed a level's queue is generated from.
 *
 * `gen.seed` wins over the top-level `seed`. `genWriter` always writes the two
 * together and they must agree, so preferring the proof's own copy is a no-op
 * on a well-formed level — and on a malformed one it keeps the queue tied to
 * the config it is resolved alongside rather than to a hand-edited `seed:`.
 *
 * Levels with no `gen` fall through to {@link getLevelSeed} (`level.seed`, else
 * {@link DEFAULT_LEVEL_SEED}), unchanged.
 */
export function resolveLevelSeed(level: LevelQueueSource): number {
  return level?.gen?.seed ?? getLevelSeed(level);
}

/**
 * Both queue-generation inputs at once — the call every audit / solver / runtime
 * path should make so the in-process and worker paths cannot drift apart.
 */
export function resolveLevelQueueParams(
  level: LevelQueueSource,
  fallbackLevelNum: number,
): LevelQueueParams {
  return {
    difficulty: resolveLevelDifficulty(level, fallbackLevelNum),
    seed: resolveLevelSeed(level),
  };
}

// Re-exported so a consumer needs exactly one import to resolve a level's queue.
export { getLevelSeed, DEFAULT_LEVEL_SEED } from './types';
